<script setup>
import UcBtn from "@/components/UC/UcBtn.vue"
import { useGlobalStore } from "@/stores/global"
import LogotypeShield from "@/assets/images/vector/logotype-shield.svg"

const runtimeConfig = useRuntimeConfig()
const store = useGlobalStore()
const user = useSupabaseUser()
const { auth } = useSupabaseClient()
const { $toast } = useNuxtApp()

const userLoggedIn = computed(() => user.value !== null)
const userNotLoggedIn = computed(() => user.value === null)

function onNavIconClick() {
  store.appNavDrawer = !store.appNavDrawer
}

async function onClickLogOut() {
  try {
    // { scope: "local" } signs the user out of the current session only.
    // Without this option, the user would be signed out of all sessions
    // (on all devices).
    // https://supabase.com/docs/guides/auth/signout?queryGroups=language&language=js#sign-out-and-scopes
    const { error } = await auth.signOut({ scope: "local" })
    if (error) throw error
    user.value = null
    $toast.success("You have now been logged out.", {
      timeout: 2000,
    })
  } catch (error) {
    console.error(error)
    $toast.error("Error logging out.")
  } finally {
    // Navigate to "/" if current route is protected.
    const route = useRoute()
    if (route.path.startsWith("/account")) {
      navigateTo("/")
    }
  }
}
</script>

<template>
  <v-app-bar absolute density="compact" flat class="mb-6">
    <v-toolbar-title>
      <NuxtLink to="/" class="logo-link" aria-label="UnitCrunch">
        <LogotypeShield
          class="logotype-shield"
          :font-controlled="false"
          aria-label="UnitCrunch"
        />
      </NuxtLink>
    </v-toolbar-title>

    <UcBtn
      to="/"
      exact
      variant="text"
      size="small"
      rounded="0"
      class="d-none d-sm-flex"
      data-cy="btn-app-bar-crunch"
    >
      Crunch
    </UcBtn>

    <UcBtn
      to="/manage-profiles"
      variant="text"
      size="small"
      rounded="0"
      class="d-none d-sm-flex"
      data-cy="btn-app-bar-manage-profiles"
    >
      Manage profiles
    </UcBtn>

    <!-- TODO: Remove condition before auth goes live. -->
    <template
      v-if="['dev', 'ci'].includes(runtimeConfig.public.appEnvironment)"
    >
      <UcBtn
        v-if="userNotLoggedIn"
        to="/login"
        data-cy="link-login"
        variant="text"
        size="small"
        rounded="0"
        class="d-none d-sm-flex"
        >Log in</UcBtn
      >

      <UcBtn
        v-if="userNotLoggedIn"
        to="/sign-up"
        data-cy="link-sign-up"
        variant="text"
        size="small"
        rounded="0"
        class="d-none d-sm-flex"
        >Sign up</UcBtn
      >

      <UcBtn
        v-if="userLoggedIn"
        data-cy="link-log-out"
        variant="text"
        size="small"
        rounded="0"
        class="d-none d-sm-flex"
        @click="onClickLogOut"
        >Log out</UcBtn
      >
    </template>

    <v-app-bar-nav-icon
      aria-label="menu"
      data-cy="btn-app-bar-menu"
      @click="onNavIconClick"
    />
  </v-app-bar>
</template>

<style lang="scss" scoped>
.logo-link {
  display: flex;
  align-items: center;
  color: white;
  text-decoration: none;
}

.logotype-shield {
  height: 18px;
}
</style>
